import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import { FaTrash, FaEdit, FaPlus, FaTimes, FaChevronLeft, FaChevronRight, FaExclamationTriangle, FaImage } from "react-icons/fa";
import { HiOutlineSearch, HiOutlineDocumentText, HiOutlineCalendar } from "react-icons/hi";
import { MdRemoveRedEye, MdEventNote } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import AddBlog from "./AddBlog";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import Cookies from "js-cookie";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Blog = () => {
  // Sidebar state and toggle
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // ALL ORIGINAL STATE
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'alphabetical'
  const [imageLightbox, setImageLightbox] = useState(null);
  const itemsPerPage = 10;
  const authToken = Cookies.get("authToken");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Calculate blog statistics
  const stats = useMemo(() => {
    return {
      total: blogs.length,
      withImages: blogs.filter(b => b.image).length
    };
  }, [blogs]);

  // Filter and sort blogs
  useEffect(() => {
    let filtered = [...blogs];

    // Apply search filter
    if (debouncedSearchTerm.trim() !== "") {
      filtered = filtered.filter(blog =>
        blog.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        blog.article?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === 'oldest') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      } else {
        return (a.title || '').localeCompare(b.title || '');
      }
    });

    setFilteredBlogs(filtered);
    setCurrentPage(1);
  }, [debouncedSearchTerm, blogs, sortBy]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllBlog`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();

        if (responseData.success) {
          const blogsData = responseData.data?.blogs || [];

          if (Array.isArray(blogsData)) {
            setBlogs(blogsData);
            setFilteredBlogs(blogsData);

            if (blogsData.length > 0) {
              // Use a unique toast ID to prevent duplicates
              
            }
          } else {
            console.error("Blogs data is not an array:", blogsData);
            setBlogs([]);
            setFilteredBlogs([]);
          }
          setError(null);
        } else {
          throw new Error(responseData.message || "Failed to fetch blogs");
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
        setError("Failed to load blogs: " + err.message);
        setBlogs([]);
        setFilteredBlogs([]);
        toast.error("Failed to load blogs");
      } finally {
        setLoading(false);
      }
    };

    if (authToken) {
      fetchBlogs();
    } else {
      setError("Authentication token not found");
      setLoading(false);
    }
  }, [authToken]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentData = filteredBlogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);

  const openModal = (blog) => {
    setSelectedBlog(blog);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBlog(null);
  };

  const openAddModal = () => {
    setEditingBlog(null);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setEditingBlog(null);
  };

  const handleAddBlog = (newBlog) => {
    setBlogs([newBlog, ...blogs]);
    toast.success("Blog added successfully");
  };

  const openEditModal = (blog) => {
    setEditingBlog(blog);
    setShowAddModal(true);
  };

  const handleUpdateBlog = (updatedBlog) => {
    setBlogs((prevBlogs) =>
      prevBlogs.map((b) => (b._id === updatedBlog._id ? updatedBlog : b))
    );
    toast.success("Blog updated successfully");
  };

  const openDeleteModal = (blog) => {
    setBlogToDelete(blog);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setBlogToDelete(null);
    setIsDeleting(false);
  };

  const handleDeleteBlog = async () => {
    if (!blogToDelete) return;

    try {
      setIsDeleting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/deleteBlog/${blogToDelete._id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        setBlogs((prevBlogs) =>
          prevBlogs.filter((blog) => blog._id !== blogToDelete._id)
        );
        toast.success("Blog deleted successfully");
        closeDeleteModal();
      } else {
        throw new Error(result.message || "Failed to delete blog");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.message || "Failed to delete blog");
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <motion.div
      whileHover={{ y: -2 }}
      className="bg-white rounded-lg p-4 shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </motion.div>
  );

  const renderSkeletonCards = () => {
    return Array(3).fill(0).map((_, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-start space-x-3">
          <Skeleton width={64} height={64} className="rounded" />
          <div className="flex-1">
            <Skeleton width="80%" height={20} />
            <Skeleton width="40%" height={16} className="mt-2" />
            <div className="flex justify-end gap-2 mt-3">
              <Skeleton width={32} height={32} circle />
              <Skeleton width={32} height={32} circle />
            </div>
          </div>
        </div>
      </div>
    ));
  };

  const renderSkeletonRows = () => {
    return Array(5)
      .fill(0)
      .map((_, index) => (
        <tr key={`skeleton-${index}`} className="even:bg-gray-50">
          <td className="px-6 py-4"><Skeleton width={30} /></td>
          <td className="px-6 py-4"><Skeleton width={150} /></td>
          <td className="px-6 py-4"><Skeleton width={200} /></td>
          <td className="px-6 py-4"><Skeleton width={80} height={60} /></td>
          <td className="px-6 py-4">
            <div className="flex gap-2">
              <Skeleton width={32} height={32} circle />
              <Skeleton width={32} height={32} circle />
            </div>
          </td>
        </tr>
      ));
  };

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 px-4"
    >
      <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
        <HiOutlineDocumentText className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {searchTerm ? 'No blogs found' : 'No blogs yet'}
      </h3>
      <p className="text-gray-500 mb-6">
        {searchTerm
          ? `No blogs matching "${searchTerm}"`
          : 'Create your first blog post to share with your audience'}
      </p>
      {searchTerm && (
        <button
          onClick={() => setSearchTerm('')}
          className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <FaTimes className="w-4 h-4 mr-2" />
          Clear search
        </button>
      )}
    </motion.div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
      <ToastContainer position="top-right" autoClose={3000} />

      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/blog" />

      <div className="flex-1 w-0">
        <Header toggleSidebar={toggleSidebar} />

        <main className="p-4 lg:p-6 overflow-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-gray-200"
          >
            {/* Header with stats */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <HiOutlineDocumentText className="w-8 h-8 text-gray-600" />
                    Blog Management
                  </h1>
                  <p className="text-sm text-gray-500 mt-1">
                    {filteredBlogs.length} blog{filteredBlogs.length !== 1 ? 's' : ''} found
                    {searchTerm && ` for "${searchTerm}"`}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search blogs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white text-gray-700 transition-shadow"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <FaTimes className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <button
                    onClick={openAddModal}
                    className="w-full sm:w-auto bg-gray-800 hover:bg-gray-900 text-white px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm transition-colors shadow-sm"
                  >
                    <FaPlus /> Add Blog
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              {!loading && blogs.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <StatCard title="Total Blogs" value={stats.total} icon={HiOutlineDocumentText} color="gray" />
                  <StatCard title="With Images" value={stats.withImages} icon={FaImage} color="blue" />
                </div>
              )}
            </div>

            {/* Sort Options */}
            <div className="flex justify-end mb-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-gray-700"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg"
              >
                <p className="text-red-600 text-sm">{error}</p>
              </motion.div>
            )}

            {/* Mobile Card View */}
            <div className="block lg:hidden">
              <AnimatePresence mode="wait">
                {loading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {renderSkeletonCards()}
                  </motion.div>
                ) : currentData.length === 0 ? (
                  renderEmptyState()
                ) : (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    {currentData.map((blog, index) => (
                      <motion.div
                        key={blog._id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start space-x-3">
                          <div
                            className="relative group cursor-pointer"
                            onClick={() => blog.image && setImageLightbox(blog.image)}
                          >
                            <img
                              src={blog.image}
                              alt={blog.title}
                              className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "https://via.placeholder.com/80";
                              }}
                            />
                            {blog.image && (
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                <span className="text-white opacity-0 group-hover:opacity-100 text-xs">Preview</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-800">{blog.title}</h3>
                                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                  <HiOutlineCalendar className="w-3 h-3" />
                                  {formatDate(blog.createdAt)}
                                </p>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => openModal(blog)}
                                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="View article"
                                >
                                  <MdRemoveRedEye size={16} />
                                </button>
                                <button
                                  onClick={() => openEditModal(blog)}
                                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                  title="Edit blog"
                                >
                                  <FaEdit size={16} />
                                </button>
                                <button
                                  onClick={() => openDeleteModal(blog)}
                                  className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete blog"
                                >
                                  <FaTrash size={16} />
                                </button>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-2 mt-2">
                              {blog.article}
                            </p>
                            <button
                              className="text-xs text-gray-500 hover:text-gray-700 mt-1 font-medium"
                              onClick={() => openModal(blog)}
                            >
                              Read full article →
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Article</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      renderSkeletonRows()
                    ) : currentData.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12">
                          {renderEmptyState()}
                        </td>
                      </tr>
                    ) : (
                      currentData.map((blog, index) => (
                        <motion.tr
                          key={blog._id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm font-medium text-gray-800">{blog.title}</span>
                            <p className="text-xs text-gray-400 mt-1">{formatDate(blog.createdAt)}</p>
                          </td>
                          <td className="px-6 py-4 max-w-xs">
                            <p className="text-sm text-gray-600 line-clamp-2">{blog.article}</p>
                            <button
                              onClick={() => openModal(blog)}
                              className="text-xs text-gray-500 hover:text-gray-700 mt-1 font-medium"
                            >
                              Read more →
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="relative group">
                              <img
                                src={blog.image}
                                alt={blog.title}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-200 cursor-pointer"
                                onClick={() => blog.image && setImageLightbox(blog.image)}
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "https://via.placeholder.com/80";
                                }}
                              />
                              {blog.image && (
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center cursor-pointer"
                                  onClick={() => setImageLightbox(blog.image)}>
                                  <span className="text-white opacity-0 group-hover:opacity-100 text-xs">Preview</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(blog.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => openEditModal(blog)}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Edit blog"
                              >
                                <FaEdit size={18} />
                              </button>
                              <button
                                onClick={() => openDeleteModal(blog)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete blog"
                              >
                                <FaTrash size={18} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredBlogs.length > itemsPerPage && (
              <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredBlogs.length)} of{' '}
                  {filteredBlogs.length} blogs
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </main>
      </div>

      {/* Blog Details Modal */}
      <AnimatePresence>
        {showModal && selectedBlog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-4 lg:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-800">
                  {selectedBlog.title}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {selectedBlog.image && (
                <div className="mb-4">
                  <img
                    src={selectedBlog.image}
                    alt={selectedBlog.title}
                    className="w-full max-h-64 object-contain rounded-lg border border-gray-200 cursor-pointer"
                    onClick={() => setImageLightbox(selectedBlog.image)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/800x400";
                    }}
                  />
                </div>
              )}

              <div className="prose max-w-none mb-4">
                <p className="text-sm lg:text-base text-gray-700 whitespace-pre-line leading-relaxed">
                  {selectedBlog.article}
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={closeModal}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Lightbox */}
      <AnimatePresence>
        {imageLightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex justify-center items-center p-4 z-[60]"
            onClick={() => setImageLightbox(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-4xl max-h-[90vh]"
            >
              <img
                src={imageLightbox}
                alt="Blog"
                className="w-full h-full object-contain rounded-lg"
              />
              <button
                onClick={() => setImageLightbox(null)}
                className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 transition-colors"
              >
                <FaTimes size={20} />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Blog Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddBlog
            blog={editingBlog}
            onClose={closeAddModal}
            onAddBlog={handleAddBlog}
            onUpdateBlog={handleUpdateBlog}
            authToken={authToken}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && blogToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-4 z-50"
            onClick={closeDeleteModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.3 }}
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg lg:text-xl font-bold text-gray-800">Delete Blog</h3>
                <button
                  onClick={closeDeleteModal}
                  className="text-gray-500 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100 transition-colors"
                  disabled={isDeleting}
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <FaExclamationTriangle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">This action cannot be undone.</p>
                </div>
                <p className="text-sm lg:text-base text-gray-700">
                  Are you sure you want to delete the blog{' '}
                  <span className="font-semibold text-gray-800">"{blogToDelete.title}"</span>?
                </p>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closeDeleteModal}
                  className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm transition-colors"
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteBlog}
                  className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center text-sm transition-colors disabled:opacity-50"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Delete Blog"
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Blog;